import { useQuery, useQueryClient } from "@tanstack/react-query";
import { startAuthentication } from "@simplewebauthn/browser";
import { useEffect, useRef, useState } from "react";
import { Wordmark } from "../components/Brand.tsx";
import { Icon } from "../components/Icon.tsx";
import { api, ApiError } from "../lib/api.ts";
import { useSignOut, type Me } from "../lib/session.tsx";
import { isDark } from "../lib/theme.tsx";

declare global { interface Window { google?: any } }

export function SignIn() {
  const qc = useQueryClient();
  const cfg = useQuery({ queryKey: ["auth-config"], queryFn: () => api.get("/auth/config") });
  const [step, setStep] = useState<"start" | "code">("start");
  const [email, setEmail] = useState(localStorage.getItem("gcrm-email") ?? "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const googleDiv = useRef<HTMLDivElement>(null);

  const done = async () => { await qc.invalidateQueries({ queryKey: ["me"] }); };
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr(null);
    try { await fn(); } catch (e) { setErr(e instanceof ApiError ? e.message : "That didn't work. Check your signal and try again."); }
    finally { setBusy(false); }
  };

  // Google sign-in for Michael and Zoe, when a client id is set up (on the server).
  useEffect(() => {
    const id = cfg.data?.google_client_id;
    if (!id || !googleDiv.current) return;
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.onload = () => {
      window.google?.accounts.id.initialize({ client_id: id, callback: (r: { credential: string }) => run(async () => { await api.post("/auth/google", { credential: r.credential }); await done(); }) });
      window.google?.accounts.id.renderButton(googleDiv.current, { theme: isDark() ? "filled_black" : "outline", size: "large", shape: "rectangular", width: 320, text: "signin_with" });
    };
    document.head.appendChild(s);
    return () => { s.remove(); };
  }, [cfg.data?.google_client_id]);

  const passkey = () => run(async () => {
    const opts = await api.post("/auth/passkey/options");
    const response = await startAuthentication({ optionsJSON: opts as any });
    await api.post("/auth/passkey", { response });
    await done();
  });

  const sendCode = () => run(async () => {
    localStorage.setItem("gcrm-email", email.trim());
    const r: any = await api.post("/auth/code", { email });
    setInfo(r.message);
    setStep("code");
  });

  const verify = () => run(async () => {
    await api.post("/auth/code/verify", { email, code });
    await done();
  });

  return (
    <div className="signin">
      <Wordmark className="wm" />
      <div className="signin__card">
      <h1>Sign in</h1>
      <p>Grassick CRM. Michael and Zoe use Google; agents use a passkey on their phone or a one-time code.</p>

      {err && <div className="banner danger" style={{ margin: "0 0 16px" }}>{err}</div>}

      {step === "start" && (
        <>
          <button className="btn block" onClick={passkey} disabled={busy}><Icon name="key" />Sign in with a passkey</button>
          {cfg.data?.google_client_id
            ? <div ref={googleDiv} style={{ marginTop: 12, display: "flex", justifyContent: "center" }} />
            : <button className="btn ghost block" style={{ marginTop: 12 }} disabled title="Set up on the server">Google sign-in (set up on the server)</button>}
          <div className="or">OR A ONE-TIME CODE</div>
          <label className="field"><span>Your email</span>
            <input className="input" type="email" inputMode="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && email && sendCode()} placeholder="you@example.com" />
          </label>
          <button className="btn white block" onClick={sendCode} disabled={busy || !email.includes("@")}>Get a code</button>
          <button className="btn ghost block" style={{ marginTop: 10 }} onClick={() => { localStorage.setItem("gcrm-email", email.trim()); setStep("code"); setInfo("Enter the invite code Michael gave you."); }} disabled={!email.includes("@")}>
            I have an invite code from Michael
          </button>
        </>
      )}

      {step === "code" && (
        <>
          {info && <div className="banner" style={{ margin: "0 0 16px" }}>{info}</div>}
          <label className="field"><span>6-digit code for {email}</span>
            <input className="input code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} onKeyDown={(e) => e.key === "Enter" && code.length === 6 && verify()} autoFocus />
          </label>
          <button className="btn block" onClick={verify} disabled={busy || code.length !== 6}>Sign in</button>
          <button className="btn ghost block" style={{ marginTop: 10 }} onClick={() => { setStep("start"); setCode(""); setErr(null); }}>Back</button>
        </>
      )}
      </div>

      {cfg.data?.dev_outbox && <>
        <DevOutbox />
        <div className="fine">Local test copy with made-up records. This build sends nothing: codes appear in the dev outbox below instead of an email.</div>
      </>}
    </div>
  );
}

/** Local builds only: shows the sign-in codes this copy would have emailed. Nothing is sent. */
function DevOutbox() {
  const q = useQuery({ queryKey: ["dev-outbox"], queryFn: () => api.get("/dev/outbox"), refetchInterval: 2000 });
  const items = q.data?.items ?? [];
  return (
    <div className="banner blue" style={{ margin: "24px 0 0", flexDirection: "column" }}>
      <b>Dev outbox (local only, nothing sent)</b>
      {items.length === 0 ? <span>Ask for a code and it shows up here.</span>
        : items.slice(0, 3).map((i: any) => <span key={i.at} className="num">{i.to}: <b>{i.body.slice(0, 6)}</b></span>)}
    </div>
  );
}

export function DevicePending({ me }: { me: Me }) {
  const qc = useQueryClient();
  const signOut = useSignOut();
  useEffect(() => {
    const t = setInterval(() => qc.invalidateQueries({ queryKey: ["me"] }), 10_000);
    return () => clearInterval(t);
  }, [qc]);
  return (
    <div className="signin">
      <Wordmark className="wm" />
      <div className="signin__card">
        <h1>Waiting for approval</h1>
        <p>Hi {me.user.name}. This is a new phone or computer, so Michael needs to approve it once before it can open the CRM. It'll open here by itself when he does.</p>
        <div className="banner" style={{ margin: 0 }}><Icon name="devices" size={18} /><span>Device id ends <b className="num">{me.device.id.slice(-6)}</b>. Michael approves it in Settings → Devices.</span></div>
        <button className="btn ghost block" style={{ marginTop: 18 }} onClick={() => qc.invalidateQueries({ queryKey: ["me"] })}>Check again</button>
        <button className="btn ghost block" style={{ marginTop: 10 }} onClick={signOut}>Sign out</button>
      </div>
    </div>
  );
}
